/**
 * "Entrar com Instagram" — login empresarial do Instagram (API oficial, app
 * "Direct ChatPlus"). Abre a página do Conector (`/instagram`), que vai ao
 * Instagram, volta com o `code` e entrega pra API deste servidor; a API troca
 * pelo token de 60 dias e cria o canal. O resultado volta por postMessage.
 *
 * O Conector é UM endereço pra todos os servidores (é o redirect_uri
 * cadastrado no app) — por isso recebe `api_base` de quem o abriu.
 */

export interface InstagramLoginResult {
  whatsapp_number_id: number;
  username: string;
}

export class InstagramLoginCancelledError extends Error {
  constructor() {
    super('Login do Instagram cancelado');
  }
}

/**
 * `closed`: a janela fechou sem avisar o resultado (ex.: o vínculo com o
 * painel se perdeu no caminho). O canal pode ter sido criado mesmo assim —
 * quem chama recarrega a lista de canais.
 */
export type InstagramLoginOutcome =
  | { status: 'connected'; result: InstagramLoginResult }
  | { status: 'closed' };

export async function openInstagramLogin(
  ticket: string,
  label: string,
  /** Reconectar este canal: o login precisa ser da conta dele. */
  targetChannelId?: number,
): Promise<InstagramLoginOutcome> {
  // `||` (não `??`): build sem o arg deixa a var como string vazia.
  const loginUrl =
    (import.meta.env.VITE_INSTAGRAM_LOGIN_URL as string | undefined) ||
    'https://meta.chatplusbr.com/instagram';
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1';
  const apiBaseAbs = apiBase.startsWith('http') ? apiBase : `${window.location.origin}${apiBase}`;

  const url = new URL(loginUrl);
  url.searchParams.set('ticket', ticket);
  url.searchParams.set('api_base', apiBaseAbs);
  url.searchParams.set('opener_origin', window.location.origin);
  url.searchParams.set('label', label);
  if (targetChannelId) url.searchParams.set('target_channel_id', String(targetChannelId));
  const appId = import.meta.env.VITE_INSTAGRAM_APP_ID as string | undefined;
  if (appId) url.searchParams.set('app_id', appId);

  const popup = window.open(url.toString(), 'chatplus-instagram-login', 'width=520,height=720');
  if (!popup) {
    throw new Error(
      'Não foi possível abrir a janela do Instagram. Verifique se o bloqueador de pop-ups está desligado.',
    );
  }

  const loginHost = new URL(loginUrl).hostname;
  return new Promise<InstagramLoginOutcome>((resolve, reject) => {
    let done = false;
    const finish = (): void => {
      done = true;
      window.removeEventListener('message', onMessage);
      clearInterval(poll);
    };
    const onMessage = (event: MessageEvent): void => {
      try {
        if (new URL(event.origin).hostname !== loginHost) return;
      } catch {
        return;
      }
      const data = event.data as {
        source?: string;
        type?: string;
        result?: InstagramLoginResult;
        message?: string;
      } | null;
      if (!data || data.source !== 'chatplus-conector-ig') return;
      if (data.type === 'success' && data.result) {
        finish();
        resolve({ status: 'connected', result: data.result });
      } else if (data.type === 'cancelled') {
        finish();
        reject(new InstagramLoginCancelledError());
      } else if (data.type === 'error') {
        finish();
        reject(new Error(data.message || 'Não foi possível conectar o Instagram.'));
      }
    };
    window.addEventListener('message', onMessage);
    // Fechou sem mensagem: pode ter dado certo (vínculo perdido) ou o usuário
    // desistiu — quem chama confere recarregando a lista de canais.
    const poll = setInterval(() => {
      if (popup.closed && !done) {
        finish();
        resolve({ status: 'closed' });
      }
    }, 500);
  });
}
